import { Notification } from '@thanx/uikit/notification'
import { Radio, RadioGroup } from '@thanx/uikit/radio'
import { Select } from '@thanx/uikit/select'
import { Text } from '@thanx/uikit/text'
import type { AvailableReward } from 'api/recoveryAi.types'
import { ReactComponent as ThanxAiSparkles } from 'assets/images/thanx-ai-sparkles.svg'
import type { Option } from 'baseui/select'
import AiGeneratedBadge from 'components/AiGeneratedBadge'
import Input from 'components/Form/Input'
import LearnMoreLink from 'components/LearnMoreLink'
import ModalContainer from 'components/ModalContainer'
import Form from 'formsy-react'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { grantRewardUrl } from 'utilities/urlUtils'

const t = buildTranslate('components.grant_reward_modal')
const validationT = buildTranslate('validations')

// The reward dropdown renders in its own layer that must stack ABOVE the modal,
// or its options are clipped behind the dialog. ModalContainer defaults the modal
// to zIndex 3, so this only needs to exceed that; 1002 matches the established
// Select-in-ModalContainer pattern (see CampaignCenter ImportModal).
const REWARD_DROPDOWN_Z_INDEX = 1002

// Loyalty points persist as a signed 32-bit integer on the BE, so cap a grant at
// that ceiling: a larger value would overflow the column (an unhandled 500) and any
// entry that big is almost certainly a fat-finger. Values above this disable Confirm
// exactly like a decimal/negative does. A tighter product-driven cap can layer on later.
export const MAX_POINTS = 2_147_483_647

// Character cap on the customer-facing grant message. Parity with
// scenes/CampaignCenter/Builder/pushCharLimits' BODY_MAX_LENGTH (240), which the
// customer-profile grant modal imports — kept as a local constant here because
// src/components must not import from src/scenes. 240 is the iOS/Android
// rich-push expanded-body display ceiling; keep the two in lockstep if that
// changes.
const MESSAGE_MAX_LENGTH = 240

// Textarea height for the message, per the design — tall enough for a short
// paragraph without pushing the dialog's actions below the fold.
const MESSAGE_ROWS = 5

// AI-252: id linking the Points field to its helper text via aria-describedby
// (WCAG 1.3.1) — the helper is rendered as a sibling Text, not through Input.
const POINTS_HELPER_ID = 'grant-points-helper'

// The row's AI badge (rendered via the shared AiGeneratedBadge, variant="created")
// carries a sparkle OUTSIDE the pill via the icon override, at the same 23×20 the
// component's own subtle marker uses so the two AI markers read at one size. The
// asset (assets/images/thanx-ai-sparkles.svg) is the same spearmint cluster the
// CaseDetail AI Summary heading carries — reused, not re-created — rather than
// AiGeneratedBadge's own TUTD-specific ic-sparkles.svg.
const AI_SPARKLE_WIDTH_PX = 23
const AI_SPARKLE_HEIGHT_PX = 20

// The two mutually exclusive grant paths the radio group switches between. A
// grant is a reward OR points, never both — the customer-profile grant modal's
// shape, which this modal now matches.
type GrantType = 'reward' | 'points'

// The resolve body MINUS the record id, which each caller adds (a conversation id
// on the RAI case detail, a rating id on the Reports rating detail). Always
// 'resolved_with_reward': the BE grants points/rewards ONLY on that type.
// `notes` and `reply_message` carry the SAME operator text on purpose, because
// the backend does two different things with them and we want both: `notes`
// becomes the granted reward's issuance_reason (the "They say: …" reward-granted
// push), while `reply_message` is persisted as the record's response and sends
// Email::Template::FeedbackResponse with Reply-To = the operator. Both resolve
// endpoints accept the pair on a `resolved_with_reward` body and handle them
// independently — nexus OrderRatings::Resolve (spec: "a reply on a granting
// resolve") and Recovery::Conversations::Resolve, whose notify_reply_email is
// likewise not gated on resolution_type.
export type GrantRewardPayload = {
  resolution_type: 'resolved_with_reward'
  reward_ids?: string[]
  points?: number
  notes?: string
  reply_message?: string
}

type Props = {
  isOpen: boolean
  // Names the customer in the heading ("Grant reward to Ada Lovelace"). Null
  // when the surface has no name for them, which falls back to the generic
  // "Grant a reward" heading rather than rendering a half-empty sentence.
  customerName: string | null
  // Plain data — the caller owns the query (and its `skip`), so this component
  // does no fetching (src/components rule).
  rewards: AvailableReward[]
  isFetchingRewards: boolean
  // Whether the caller's catalog QUERY failed (its `isError`), as opposed to
  // succeeding with nothing in it. Required, not optional-with-a-default, so no
  // call site can silently drop the state the way the Reports surface did: a
  // 404 there rendered as `no_rewards`, which is precisely how the RAI route's
  // flag-gated 404 hid in production for months (AI-1017). Distinct from the
  // `isError`/`errorMessage` pair below — that is the failed-GRANT banner at the
  // bottom of the dialog, this is the reward field's own load state, and the two
  // can be true independently.
  hasRewardsError: boolean
  // Whether this merchant runs a points experience (the caller reads
  // selectors/pointsExperience). False → no radio group and no points field at
  // all: the modal is reward-only, because points can't be granted without an
  // experience to hold them.
  showPointsOption: boolean
  // The LOGGED-IN OPERATOR's email (not the customer's) — replies to the emailed
  // grant message land there, because the BE sets it as the Reply-To. Null when
  // the current merchant user isn't in the store yet, which hides the footer
  // rather than rendering a half-empty sentence. Same contract as
  // components/ResolveWithReplyModal's prop of the same name.
  operatorEmail: string | null
  // The caller's mutation state. onConfirm resolves on success and rejects on
  // failure; this component closes itself on the former and leaves the caller's
  // isError to render the banner on the latter.
  onConfirm: (payload: GrantRewardPayload) => Promise<unknown>
  isSubmitting: boolean
  isError: boolean
  onResetError: () => void
  // Surface-specific failure copy ("resolving this case" vs "this feedback"), so
  // the shared component never hardcodes one caller's wording.
  errorMessage: string
  onClose: () => void
}

// Operator action: grant a reward (with a required customer-facing message) OR
// bonus points, and resolve the record in one step. PRESENTATIONAL — the write
// permission check lives in the calling scene, not here. Deliberately the same
// UX as the customer-profile grant modal (scenes/Users/View/GrantModal): same
// heading, same radio group, same message field and cap, same Learn more link —
// plus the required customer-facing message on both paths.
// Reuses the shared ModalContainer (Base Web Modal — owns focus-trap/ESC/aria).
const GrantRewardModal = ({
  isOpen,
  customerName,
  rewards,
  isFetchingRewards,
  hasRewardsError,
  showPointsOption,
  operatorEmail,
  onConfirm,
  isSubmitting,
  isError,
  onResetError,
  errorMessage,
  onClose,
}: Props) => {
  const [css] = useStyletron()
  const [selectedGrantType, setSelectedGrantType] =
    useState<GrantType>('reward')
  const [selectedRewardId, setSelectedRewardId] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  // Points are held as a string (the raw input value) and parsed at submit —
  // the DailyCapCard / EscalationRulesCard numeric-field precedent.
  const [points, setPoints] = useState('')
  // Formsy's verdict on the message field's validation rules (maxLength AND
  // required), which gates Confirm the same way the customer-profile modal's
  // formIsValid does.
  const [isFormValid, setIsFormValid] = useState(true)

  // Without a points experience there is no points path to be on, so the
  // selection can never leave 'reward' — defense-in-depth against a caller
  // whose experience disappears while the modal is open.
  const grantType: GrantType = showPointsOption ? selectedGrantType : 'reward'
  const isGrantingPoints = grantType === 'points'

  // AI-1044: the option is the reward's PROGRAM NAME and nothing else — the same
  // string the customer-profile grant dropdown renders (scenes/Users/View/
  // GrantModal, whose options are campaigns/grantable's `name`; this catalog's
  // `label` is that same name, resolved through Program::FetchGrantable). The
  // cost used to be appended ("Free Entrée — $15.00") and read as a mystery
  // figure: it is the merchant's COST, not the perceived value, and neither is
  // meaningful when choosing what to grant.
  //
  // A reward with no name is DROPPED rather than rendered: an unnamed template
  // is what put a list of "null — $2.00" rows in front of operators, and there
  // is nothing an operator can do with an option they cannot identify. The
  // backend excludes these too — this is the second line of defense, because
  // the type says `label: string` while the wire can carry a null name for a
  // legacy template whose name predates the settings→column migration.
  //
  // AI-1044 continued: an AI-authored row is tagged in-row by renderRewardOption
  // below — a spearmint "AI recommended" pill with a sparkle OUTSIDE it — rather
  // than under a group header, and sorted AHEAD of merchant-authored templates so
  // the AI offers lead the list. `isAiGenerated` is the only extra the option
  // carries; selection and the grant payload never branch on it (an AI-authored
  // reward grants exactly like any other, id and all).
  //
  // isAiAuthored primarily reads `ai_generated`, NOT `source === 'make_good'`:
  // `source` only distinguishes a materialized make-good OFFER row from a
  // template row — every template row serializes `source: 'template'`
  // regardless of who authored it (AvailableRewardEntity#source), so an
  // AI-247/AI-243 auto-provisioned TEMPLATE (ai_generated: true, source:
  // 'template') would render untagged under a `source`-only check.
  // `ai_generated` is the one signal ApprovedRewardsBody already uses to mean
  // "Thanx AI authored" — reusing it here is what keeps the two surfaces from
  // disagreeing on the same reward. The `|| source === 'make_good'` half is
  // defense-in-depth, not the primary signal: nexus's make_good_offer_rows always
  // pairs the two on the wire, but `ai_generated` is optional on the FE type, so a
  // materialized make-good offer that somehow arrived without it still tags and
  // sorts correctly rather than silently degrading.
  const isAiAuthored = (reward: AvailableReward) =>
    reward.ai_generated === true || reward.source === 'make_good'

  const options = useMemo(() => {
    const toOption = (reward: AvailableReward) => ({
      value: reward.id,
      label: reward.label,
      isAiGenerated: isAiAuthored(reward),
    })
    const activeRewards = rewards.filter(
      reward => reward.active && !!reward.label?.trim()
    )
    // AI-authored rewards first (make-good offers and any AI-generated
    // template), merchant-authored templates second — a stable order regardless
    // of how the API interleaves the two. WITHIN the AI-authored group, order by
    // the dollar cap ASCENDING ($3 → $140), not the API's order: available_rewards
    // returns make-good offers in `offerable`'s order, which is effectively
    // alphabetical by name and so reads "$10, $100, … $140, $20, $3". value_cents
    // IS that cap for a make-good offer (nexus available_rewards.rb sets it to
    // offer.up_to_cents), but an AI-authored TEMPLATE has no up_to_cents and so no
    // value_cents at all — those rows sort to the tail of the AI group, in
    // whatever order the API returned them (no other price signal exists for
    // them; this is an accepted consequence of widening the group beyond
    // make-good offers, not a defect).
    //
    // MAX_SAFE_INTEGER, not POSITIVE_INFINITY: Array.prototype.sort's comparator
    // contract is undefined for a NaN return, which is exactly what
    // `Infinity - Infinity` produces when TWO rows both lack value_cents —
    // reachable the moment more than one AI-authored template is in the same
    // catalog. A finite fallback keeps the subtraction finite (0) so equal-rank
    // rows compare equal, per spec, instead of relying on V8's incidental NaN→0
    // coercion.
    const byValueAsc = (a: AvailableReward, b: AvailableReward) =>
      (a.value_cents ?? Number.MAX_SAFE_INTEGER) -
      (b.value_cents ?? Number.MAX_SAFE_INTEGER)
    return [
      ...activeRewards.filter(isAiAuthored).sort(byValueAsc).map(toOption),
      ...activeRewards.filter(reward => !isAiAuthored(reward)).map(toOption),
    ]
  }, [rewards])
  const hasRewards = options.length > 0

  // Points are whole loyalty points (the BE field is an Integer), so require a
  // positive integer within the int32 ceiling; a blank / zero / negative /
  // decimal / over-cap / non-numeric value keeps Confirm disabled.
  const parsedPoints = Number(points)
  const hasPoints =
    Number.isInteger(parsedPoints) &&
    parsedPoints > 0 &&
    parsedPoints <= MAX_POINTS
  // MEMBERSHIP, not just "something is selected and the catalog is non-empty".
  // The catalog refetches while this modal is open (any resolve invalidates it)
  // and nothing here resets selectedRewardId when `rewards` changes — only the
  // close edge and a grant-type switch do. A reward can leave `options` two
  // ways: it goes inactive, or it loses its name (dropped above). Either way the
  // id lingers in state, and gating on `hasRewards` alone left Confirm enabled
  // and submitted a reward_ids the operator could no longer see. Still subsumes
  // the AI-119 reward-only guard: an empty catalog can't contain the selection.
  const hasReward = options.some(option => option.value === selectedRewardId)
  // Exactly one path's readiness (points vs reward) matters, but the message
  // field's validation (isFormValid — maxLength AND required) now gates BOTH
  // paths, because the message field is shared across them.
  const canProceed = (isGrantingPoints ? hasPoints : hasReward) && isFormValid

  // The ONE disabled-Confirm reason worth a tooltip: the other two
  // (no reward selected / no points entered) are already visually obvious —
  // an empty required field sits right on screen — so they stay silent.
  const messageRequiredButBlank = !message.trim()

  const handleRewardChange = useCallback(
    (selected: { value: string }[]) =>
      setSelectedRewardId(selected[0] ? selected[0].value : null),
    []
  )

  // Switching paths clears only the OTHER path's field, so a reward id or a points
  // value can never leak into the payload built for the other. The customer-facing
  // message is a shared field (rendered for both paths), so it deliberately
  // persists across a switch — and its validation verdict (isFormValid) persists
  // with it, since the field is never unmounted here.
  const handleGrantTypeChange = useCallback((next: GrantType) => {
    setSelectedGrantType(next)
    if (next === 'points') {
      setSelectedRewardId(null)
      return
    }
    setPoints('')
  }, [])

  // Every draft the operator entered, back to its initial value. Shared by the
  // Cancel/confirm path (handleClose) and the isOpen-driven reset below, so the
  // two can't drift.
  const resetDraft = useCallback(() => {
    setSelectedGrantType('reward')
    setSelectedRewardId(null)
    setMessage('')
    setPoints('')
    setIsFormValid(true)
  }, [])

  // Reset on the CLOSED edge, not only in handleClose: the calling scenes close
  // this modal directly (setIsGrantModalOpen(false) in their record-change
  // effect) when the viewed record changes, which never runs handleClose. Base
  // Web's Modal does unmount its children once fully closed — so the DOM fields
  // go away — but THIS component stays mounted (the scene renders it for the
  // whole life of an open record), so its useState survives that unmount and a
  // reopen would re-seed the fields from the previous record's draft.
  //
  // Deliberately NOT calling onResetError() here: both scenes already reset
  // their mutation when the record changes, and handleClose resets it on the
  // Cancel/confirm path — calling it again on every close would be redundant
  // work on a shared mutation instance.
  useEffect(() => {
    if (isOpen) return
    resetDraft()
  }, [isOpen, resetDraft])

  const handleClose = () => {
    // AI-206: ignore close requests (Cancel / backdrop / ESC) while a grant is in
    // flight — the POST can't be aborted client-side and would resolve and
    // re-invoke this via handleConfirm's .then, double-firing onResetError()/
    // onClose(), and a mid-mutation reset() has murky RTK semantics. Mirrors
    // RestrictCustomerModal.
    if (isSubmitting) return
    resetDraft()
    // Clear any failed-grant error so a stale banner doesn't show on reopen —
    // this modal's own state outlives Base Web's unmount of the dialog contents.
    onResetError()
    onClose()
  }

  // Dropdown option content. An AI-authored row (isAiAuthored above) carries the
  // shared AiGeneratedBadge (variant="created" → "AI recommended", the same copy
  // MakeGoodLadder/ApprovedRewardsBody use for the same object — see AI-1044's
  // cross-surface note) pushed to the row's trailing edge; a template row is its
  // plain program name. Only the OPEN list shows this: the collapsed selected
  // value uses baseui's getValueLabel (the plain label), so the chip stays
  // uncluttered. `option` is baseui's loosely-typed Option, so isAiGenerated
  // arrives via its index signature.
  const renderRewardOption = (option?: Option) => {
    if (!option) return null
    if (option.isAiGenerated !== true) return option.label
    return (
      <span
        className={css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          width: '100%',
        })}
      >
        <span>{option.label}</span>
        <AiGeneratedBadge
          variant="created"
          icon={
            // Decorative — the pill text already conveys the AI-authored meaning.
            <ThanxAiSparkles
              aria-hidden="true"
              className={css({
                width: `${AI_SPARKLE_WIDTH_PX}px`,
                height: `${AI_SPARKLE_HEIGHT_PX}px`,
                flexShrink: 0,
              })}
            />
          }
        />
      </span>
    )
  }

  // Reward field: the dropdown once rewards load, a loading affordance while
  // fetching, a failure message if the catalog query broke, or an empty-state
  // message. Extracted to avoid a multi-branch ternary. The failure branch sits
  // AHEAD of the empty state on purpose — collapsing the two is what made the
  // AI-1017 404 look like a merchant with nothing to grant — and BEHIND the
  // loading one, so an in-flight retry reads as loading rather than as the
  // previous rejection RTK Query still reports through isError.
  const renderRewardField = () => {
    if (hasRewards) {
      return (
        <Select
          aria-label={t('select_reward')}
          options={options}
          value={options.find(option => option.value === selectedRewardId)}
          onChange={handleRewardChange}
          getOptionLabel={({ option }) => renderRewardOption(option)}
          placeholder={t('reward_placeholder')}
          zIndex={REWARD_DROPDOWN_Z_INDEX}
          maxDropdownHeight="200px"
          // The uikit Select defaults clearable→false; opt back in so an operator
          // who selected a reward just to browse the catalog can clear it again.
          // handleRewardChange already maps the empty selection to null.
          clearable
        />
      )
    }
    if (isFetchingRewards) {
      return <Text.BodyText4 color="grey70">{t('loading')}</Text.BodyText4>
    }
    if (hasRewardsError) {
      // `negative` is the uikit theme's error token (TextColorKeys.negative) —
      // the failure has to look different from the neutral grey70 empty state,
      // not just read differently. No role="alert" here: this already renders
      // inside the field's aria-live="polite" region below, and nesting an
      // assertive region inside a polite one double-announces the same string.
      return (
        <Text.BodyText4 color="negative">{t('rewards_error')}</Text.BodyText4>
      )
    }
    return <Text.BodyText4 color="grey70">{t('no_rewards')}</Text.BodyText4>
  }

  // The payload for the live path only: points OR reward_ids, never both and never
  // an empty key. The required customer message rides on EITHER path as the same
  // trimmed text under both keys — reply_message emails it
  // (Email::Template::FeedbackResponse / RecoveryResponse, Reply-To the operator)
  // and notes keeps the reward-granted push saying "They say: …" (on the reward
  // path).
  const buildPayload = (): GrantRewardPayload | null => {
    const trimmed = message.trim()
    const messageKeys = trimmed
      ? { notes: trimmed, reply_message: trimmed }
      : {}
    if (isGrantingPoints) {
      if (!hasPoints) return null
      return {
        resolution_type: 'resolved_with_reward',
        points: parsedPoints,
        ...messageKeys,
      }
    }
    if (!hasReward || !selectedRewardId) return null
    return {
      resolution_type: 'resolved_with_reward',
      reward_ids: [selectedRewardId],
      ...messageKeys,
    }
  }

  const handleConfirm = () => {
    const payload = buildPayload()
    if (!payload) return
    onConfirm(payload)
      .then(handleClose)
      // The failure is surfaced inline via isError below; swallow the rejection
      // so it isn't an unhandled promise.
      .catch(() => {})
  }

  const title = customerName ? t('title', { name: customerName }) : t('heading')

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      // With a customer the visible heading IS the dialog's name; without one,
      // the dedicated aria_label key stands in.
      ariaLabel={customerName ? title : t('aria_label')}
      proceedText={isGrantingPoints ? t('grant_points') : t('grant_reward')}
      proceedTooltip={
        messageRequiredButBlank ? t('message_required_tooltip') : undefined
      }
      onProceed={handleConfirm}
      canProceed={canProceed}
      isLoading={isSubmitting}
      buttonGroupChildren={
        <LearnMoreLink
          url={grantRewardUrl}
          className={css({ marginRight: 'auto' })}
        />
      }
    >
      {showPointsOption && (
        <RadioGroup
          aria-label={t('grant_type_aria')}
          value={grantType}
          // The uikit RadioGroup hands back the selected radio's raw value; map
          // it onto the GrantType union rather than trusting the string.
          onChange={value =>
            handleGrantTypeChange(value === 'points' ? 'points' : 'reward')
          }
        >
          <Radio value="reward" className="pr-m">
            {t('grant_a_reward')}
          </Radio>
          <Radio value="points">{t('grant_points')}</Radio>
        </RadioGroup>
      )}

      <Form
        className="mt-m"
        onValid={() => setIsFormValid(true)}
        onInvalid={() => setIsFormValid(false)}
      >
        {isGrantingPoints ? (
          <>
            <Input
              name="points"
              type="number"
              label={t('points_label')}
              placeholder={t('points_placeholder')}
              inputProps={{ min: 1, step: 1, max: MAX_POINTS }}
              value={points}
              onChange={event => setPoints(event.currentTarget.value)}
              aria-describedby={POINTS_HELPER_ID}
              className="mb-xs"
            />
            {/* id on a wrapper (uikit Text does not forward id) so the field's
                aria-describedby can reference the helper copy. */}
            <div id={POINTS_HELPER_ID}>
              <Text.BodyText5 color="grey70">
                {t('points_helper')}
              </Text.BodyText5>
            </div>
          </>
        ) : (
          <>
            <Text.BodyText4 tag="p" bold className="mb-xs">
              {t('select_reward')}
            </Text.BodyText4>
            {/* Announce the loading → loaded/empty transition to screen readers. */}
            <div aria-live="polite" aria-atomic="true">
              {renderRewardField()}
            </div>
          </>
        )}

        {/* The required customer-facing message, shared by BOTH grant paths. */}
        <Input
          name="message"
          componentClass="textarea"
          label={t('add_message')}
          inputProps={{ rows: MESSAGE_ROWS }}
          value={message}
          onChange={event => setMessage(event.currentTarget.value)}
          className="mt-m mb-xs"
          // The grant message goes out BOTH ways: as the emailed reply
          // (reply_message) and as the body of the reward-granted push (notes). The
          // cap is the tighter of the two — the push — so it shares the campaign
          // composer's body cap (MESSAGE_MAX_LENGTH = 240) and a bare counter. See
          // nexus Notification::RewardGranted#text.
          counterStyle="bare"
          // isRequired alone only catches value === '' (this repo's custom
          // !!value rule, utilities/formsyValidators.ts — not formsy-react's
          // own check) — matchRegexp closes the whitespace-only gap so a few
          // spaces can't pass validation and then get trimmed to nothing at
          // submit. Both map to the same copy: only one of the two ever fires
          // for a given value, and the operator shouldn't see different
          // wording depending on which one caught it.
          validations={{
            maxLength: MESSAGE_MAX_LENGTH,
            isRequired: true,
            matchRegexp: /\S/,
          }}
          validationErrors={{
            maxLength: validationT('max_length', {
              maxLength: MESSAGE_MAX_LENGTH,
            }),
            isRequired: validationT('is_required'),
            matchRegexp: validationT('is_required'),
          }}
        />
        {/* Omitted when the operator's address is unknown — the sentence has no
            meaning without it. Mirrors components/ResolveWithReplyModal. */}
        {operatorEmail && (
          <Text.BodyText5 color="grey70">
            {t('reply_to_footer', { email: operatorEmail })}
          </Text.BodyText5>
        )}
      </Form>

      {isError && (
        <div className={css({ marginTop: '12px' })}>
          <Notification
            kind="negative"
            size="full"
            closeable
            onClose={onResetError}
          >
            {errorMessage}
          </Notification>
        </div>
      )}
    </ModalContainer>
  )
}

export default GrantRewardModal
